import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Link,
    useMediaQuery,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../services/api';
import { getApiErrorMessage, normalizeTeacher } from '../../services/apiUtils';

export default function Login() {

    const responsiveGrid = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [senha, setSenha] = useState("");
    const [loading, setLoading] = useState(false);

    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    async function handleLogin(e) {
        e.preventDefault();
        setLoading(true);

        try {
            const { data } = await api.post('/teacher/authenticate', {
                email,
                password: senha,
            });

            const token = data?.access_token;
            if (!token) {
                showSnackbar('Não foi possível autenticar. Tente novamente.', 'error');
                return;
            }

            login({}, token);
            const profile = await api.get('/teacher');
            login(normalizeTeacher(profile.data), token);
            navigate("/planejamento");
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, 'Ocorreu um erro ao tentar fazer login. Tente novamente.'), 'error');
            const el = document.getElementById('email');
            if (el) el.focus();
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    function openForgot() {
        setForgotEmail("");
        setForgotSent(false);
        setForgotOpen(true);
    }

    function closeForgot() {
        setForgotOpen(false);
    }

    function isEmail(val = "") {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    }

    async function handleForgotSend() {
        if (!isEmail(forgotEmail)) {
            showSnackbar("Informe um e-mail válido.", "warning");
        } else {
            setForgotLoading(true);
            try {
                const { data } = await api.post("/teacher/password-reset/request", { email: forgotEmail.trim() });

                if (data?.status === true) {
                    setForgotSent(true);
                    showSnackbar(data?.message || "Solicitação enviada.", "success");
                } else {
                    showSnackbar("E-mail não cadastrado. Digite o seu e-mail de login.", "warning");
                }
            } catch (e) {
                showSnackbar(getApiErrorMessage(e, "Falha ao enviar a redefinição. Tente novamente."), "error");
            } finally {
                setForgotLoading(false);
            }
        }
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                py: 4,
                background: (theme) =>
                    `radial-gradient(circle at top left, ${theme.palette.primary.light}22, transparent 60%),
           radial-gradient(circle at bottom right, ${theme.palette.secondary.light}22, transparent 55%),
           ${theme.palette.background.default}`,
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 1080 }}>
                <Paper
                    elevation={8}
                    sx={{
                        borderRadius: 4,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: responsiveGrid ? "column" : "row",
                        backdropFilter: "blur(8px)",
                    }}
                >
                    {/* Lado esquerdo - branding / destaque (desktop) */}
                    {!responsiveGrid && (
                        <Box
                            sx={{
                                flex: 1.1,
                                p: 4,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                background: (theme) =>
                                    `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: "primary.contrastText",
                            }}
                        >
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                    Planejamento inteligente
                                </Typography>

                                <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 360 }}>
                                    Crie <strong>Atividades, Avaliações e Planos de Aula</strong> alinhados à <strong>BNCC</strong> de forma automática.
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <Typography variant="overline" sx={{ opacity: 0.8 }}>
                                    O que você encontra aqui
                                </Typography>

                                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Typography variant="body2">
                                        • Conteúdos exclusivos gerados automaticamente
                                    </Typography>
                                    <Typography variant="body2">
                                        • Personalização por série e objetivo
                                    </Typography>
                                    <Typography variant="body2">
                                        • Economia real de tempo para professores
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 4, display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "50%",
                                        border: "2px solid",
                                        borderColor: "primary.contrastText",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 700,
                                        fontSize: 18,
                                    }}
                                >
                                    ⚡
                                </Box>

                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Produza materiais didáticos em 1 min.
                                </Typography>
                            </Box>
                        </Box>

                    )}

                    {/* Lado direito - formulário */}
                    <Box
                        sx={{
                            flex: 1,
                            p: responsiveGrid ? 3 : 4,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        {/* Título / logo compacto no mobile */}
                        <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 0.5 }}>
                            <Typography
                                variant={responsiveGrid ? "h5" : "h6"}
                                sx={{ fontWeight: 700, textAlign: responsiveGrid ? "center" : "left" }}
                            >
                                Entrar
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ textAlign: responsiveGrid ? "center" : "left" }}
                            >
                                Use suas credenciais para acessar sua conta.
                            </Typography>
                        </Box>

                        <form onSubmit={handleLogin}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    id="email"
                                    label="E-mail"
                                    type="email"
                                    fullWidth
                                    required
                                    margin="none"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    disabled={loading}
                                    autoComplete="email"
                                />

                                <TextField
                                    id="senha"
                                    label="Senha"
                                    type="password"
                                    fullWidth
                                    required
                                    margin="none"
                                    value={senha}
                                    onChange={e => setSenha(e.target.value)}
                                    disabled={loading}
                                    autoComplete="current-password"
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mt: -0.90
                                    }}
                                >
                                    <Box sx={{ flex: 1 }} />
                                    <Link
                                        sx={{ fontSize: 14 }}
                                        component="button"
                                        type="button"
                                        underline="hover"
                                        onMouseDown={(e) => e.preventDefault()}
                                        formNoValidate
                                        onClick={(e) => {
                                            e.preventDefault();
                                            openForgot();
                                        }}
                                    >
                                        Esqueci minha senha
                                    </Link>
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    sx={{
                                        mt: 1,
                                        mb: 1,
                                        py: 1.2,
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        fontSize: 15,
                                        position: "relative",
                                        textTransform: "none",
                                        "&.Mui-disabled": {
                                            backgroundColor: "rgba(160,160,160,0.6)",
                                            color: "#fff",
                                            cursor: "not-allowed",
                                        },
                                    }}
                                    disabled={loading}
                                >
                                    {loading && (
                                        <CircularProgress
                                            size={20}
                                            sx={{ color: "white", position: "absolute", left: 16 }}
                                        />
                                    )}
                                    <span style={{ marginLeft: loading ? 24 : 0 }}>
                                        {loading ? "Entrando..." : "Entrar"}
                                    </span>
                                </Button>
                            </Box>
                        </form>

                        <Divider sx={{ my: 2.5 }} />

                        <Typography align="center" variant="body2">
                            Não tem uma conta?{" "}
                            <Link
                                component="button"
                                underline="hover"
                                onClick={() => navigate("/cadastro")}
                            >
                                Criar conta
                            </Link>
                        </Typography>
                    </Box>
                </Paper>
            </Box>

            {/* Aviso mobile fixo no rodapé */}
            {responsiveGrid && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 12,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "calc(100% - 24px)",
                        maxWidth: 600,
                        bgcolor: (theme) => theme.palette.warning.light,
                        color: (theme) => theme.palette.warning.contrastText,
                        textAlign: "center",
                        py: 1,
                        px: 2,
                        borderRadius: 2,
                        boxShadow: 3,
                        zIndex: (theme) => theme.zIndex.snackbar,
                        fontWeight: 600,
                        fontSize: 14,
                        display: "none"
                    }}
                >
                    💡 Para a experiência completa, recomendamos acessar pelo computador (desktop).
                </Box>
            )}

            {/* Dialog de esqueci a senha */}
            <Dialog
                open={forgotOpen}
                onClose={closeForgot}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ m: 0, p: 2 }}>
                    Redefinir senha
                    <IconButton
                        aria-label="close"
                        onClick={closeForgot}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {!forgotSent ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                                Informe o e-mail cadastrado. Enviaremos um link para redefinição.
                            </Typography>
                            <TextField
                                label="E-mail"
                                type="email"
                                fullWidth
                                autoFocus
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                                disabled={forgotLoading}
                            />
                        </>
                    ) : (
                        <Box sx={{ py: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Solicitação enviada!
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Você receberá o link de redefinição em instantes.
                                Verifique também a caixa de spam / lixo eletrônico.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    {!forgotSent ? (
                        <Button
                            variant="contained"
                            onClick={handleForgotSend}
                            disabled={forgotLoading}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1.5,
                                "&.Mui-disabled": {
                                    backgroundColor: "rgba(160,160,160,0.6)",
                                    color: "#fff",
                                },
                            }}
                        >
                            {forgotLoading && <CircularProgress size={18} sx={{ color: "white" }} />}
                            {forgotLoading ? "Enviando..." : "Enviar"}
                        </Button>
                    ) : (
                        <Button variant="contained" onClick={closeForgot}>
                            Ok
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
